/**
 * Wishlist Context - Global state management for product wishlist
 */

import type React from 'react';
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';

import { getProductById } from '../data/products';
import type { Product } from '../types';

const WISHLIST_STORAGE_KEY = 'faeforest:wishlist';

const loadWishlistFromStorage = (): Product[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const ids: string[] = JSON.parse(
      localStorage.getItem(WISHLIST_STORAGE_KEY) ?? '[]',
    );
    return ids
      .map(getProductById)
      .filter((product): product is Product => Boolean(product));
  } catch {
    return [];
  }
};

interface WishlistContextValue {
  items: Product[];
  addToWishlist: (product: Product) => void;
  removeFromWishlist: (productId: string) => void;
  isInWishlist: (productId: string) => boolean;
  clearWishlist: () => void;
}

const WishlistContext = createContext<WishlistContextValue | undefined>(
  undefined,
);

export const WishlistProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [items, setItems] = useState<Product[]>(loadWishlistFromStorage);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    localStorage.setItem(
      WISHLIST_STORAGE_KEY,
      JSON.stringify(items.map((item) => item.id)),
    );
  }, [items]);

  const addToWishlist = useCallback((product: Product) => {
    setItems((currentItems) => {
      if (currentItems.some((item) => item.id === product.id)) {
        return currentItems;
      }
      return [...currentItems, product];
    });
  }, []);

  const removeFromWishlist = useCallback((productId: string) => {
    setItems((currentItems) =>
      currentItems.filter((item) => item.id !== productId),
    );
  }, []);

  const isInWishlist = useCallback(
    (productId: string) => items.some((item) => item.id === productId),
    [items],
  );

  const clearWishlist = useCallback(() => {
    setItems([]);
  }, []);

  const value: WishlistContextValue = {
    items,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    clearWishlist,
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = (): WishlistContextValue => {
  const context = useContext(WishlistContext);
  if (context === undefined) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};
